<template>
  <ValidationObserver ref="form">
    <ControlBar
      v-if="item"
      v-model="item"
      :readOnly="readOnly"
      @edit-click="toggleReadOnly"
      @cancel-click="onCancel($event)"
      v-stream:submit-click="onSubmit$"
      v-stream:resubmit-click="onResubmit$"
      :role="role"
      :deletedBy="user.username"
      :validate="validate"
      :title="title"
    >
    </ControlBar>
    <div v-if="item">
      <div class="setting-section-block">
        <div class="section-middle">
          <b-container>
            <b-form @submit.prevent>
              <h4>公司簡介範本</h4>
              <AGCInput
                v-if="!readOnly"
                v-model="item.title"
                name="標題"
                label="標題"
                rules="required|max:50"
                class="mb-3"
                placeholder="標題"
                required
                autofocus
                :disabled="readOnly"
              ></AGCInput>
              <div v-else class="mb-3 text-secondary">{{item.title}}</div>
              <ContentEditor
                v-model="item.content"
                name="內容"
                class="mb-3"
                :read-only="readOnly"
                :toolbar="toolbarValue"
                rules="required"
                label="內容"
                required
              ></ContentEditor>
            </b-form>
          </b-container>
        </div>
      </div>
    </div>
    <ActionSuccessModal v-model="actionSuccessModal"></ActionSuccessModal>
  </ValidationObserver>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { of } from 'rxjs';
import {
  pluck, startWith, switchMap, tap, map,
} from 'rxjs/operators';
import http from '@/helpers/http';
import { BForm, BContainer } from 'bootstrap-vue';
import cloneDeep from 'lodash.clonedeep';
import ControlBar from '@/components/company-intro-template/ControlBar.vue';
import formMixin from '@/mixins/form';
import AGCInput from '@/components/form/AGCInput.vue';
import ContentEditor from '@/components/editor/ContentEditor.vue';
import ActionSuccessModal from '@/components/ActionSuccessModal.vue';
import store, { SET_LOADING } from '@/store';
import { getErrorMsg } from '../../helpers/error-msg';
import toastSettings from '../../helpers/toast-settings';

const initialState = {
  id: '',
  title: '',
  content: '',
};

export default {
  mixins: [formMixin],
  props: {
    publicApi: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BForm,
    AGCInput,
    ContentEditor,
    BContainer,
    ControlBar,
    ActionSuccessModal,
  },
  data: () => ({
    item: null,
    title: '',
    toolbarValue: [
      `
      undo redo |
      bold italic underline |
      alignleft aligncenter alignright alignjustify |
      bullist numlist outdent indent |
      link |
    `,
    ],
    readOnly: true,
    actionSuccessModal: {
      show: false,
      msg: '成功新增公司簡介範本',
      action: 'SUBMIT_SUCCESS',
      back: {
        location: '/admin/company-intro-templates',
        text: '返回公司簡介範本',
      },
    },
    stateBeforeUpdate: '',
  }),
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    backUrl() {
      return this.role === 'admin' ? '/admin/company-intro-templates' : null;
    },
    id() {
      return this.$route.params.id;
    },
    apiUrl() {
      return '/agc-admin-api/companyintrotemplates/details/';
    },
    hideCheckStatus() {
      if (this.role === 'merchant') {
        return true;
      }
      return false;
    },
    isAdmin() {
      return this.role === 'admin' && this.$route.name;
    },
    ...mapState(['isLoading']),
  },
  domStreams: ['onSubmit$', 'onResubmit$'],
  subscriptions() {
    const getData$ = (id) => http({
      method: 'GET',
      url: this.apiUrl + id,
    });

    const submit$ = (data) => http(
      {
        method: 'POST',
        url: `/agc-${this.role}-api/companyintrotemplates/submit`,
        data,
      },
      {
        error: ({ data: { msg } }) => {
          this.$bvToast.toast(getErrorMsg(msg), {
            ...toastSettings,
            title: '提交失敗',
            toaster: 'b-toaster-top-center',
          });
        },
        next: () => {
          this.readOnly = true;
          this.$nextTick(() => {
            this.$refs.form.reset();
          });
          this.actionSuccessModal.show = true;
          this.title = this.item.title;
        },
        complete: () => {
          store.commit(SET_LOADING, false);
        },
      },
    );

    const resubmit$ = (data) => http(
      {
        method: 'POST',
        url: `/agc-${this.role}-api/companyintrotemplates/resubmit`,
        data,
      },
      {
        error: ({ data: { msg } }) => {
          this.$bvToast.toast(getErrorMsg(msg), {
            ...toastSettings,
            title: '提交失敗',
            toaster: 'b-toaster-top-center',
          });
        },
        next: () => {
          this.readOnly = true;
          this.$nextTick(() => {
            this.$refs.form.reset();
          });
          this.$bvModal.show('coupon-submit-success-modal');
          this.$bvModal.hide('coupon-preview-modal');
          this.title = this.item.title;
        },
        complete: () => {
          store.commit(SET_LOADING, false);
        },
      },
    );

    return {
      item: this.$watchAsObservable('id').pipe(
        pluck('newValue'),
        startWith(this.id),
        switchMap((id) => {
          if (!id) {
            return of(
              cloneDeep({
                ...initialState,
              }),
            );
          }
          return getData$(id);
        }),
        tap((res) => {
          this.stateBeforeUpdate = cloneDeep(res);
          this.title = res.title;
        }),
      ),
      onSubmit: this.onSubmit$.pipe(
        switchMap(() => this.validate({ isScrollToTopIfFail: true })),
        tap(() => {
          store.commit(SET_LOADING, true);
        }),
        map(() => ({
          ...this.item,
        })),
        switchMap(submit$),
      ),
      onResubmit: this.onResubmit$.pipe(
        switchMap(() => this.validate({ isScrollToTopIfFail: true })),
        tap(() => {
          store.commit(SET_LOADING, true);
        }),
        map(() => ({
          ...this.item,
        })),
        switchMap(resubmit$),
      ),
    };
  },
  methods: {
    toggleReadOnly() {
      this.readOnly = !this.readOnly;
    },
    initialize() {
      this.item = cloneDeep({
        ...initialState,
      });
      this.readOnly = false;
    },
    onCancel() {
      this.toggleReadOnly();
      this.$refs.form.reset();
      this.item = cloneDeep({
        ...this.stateBeforeUpdate,
      });
    },
  },
  mounted() {
    if (this.$route.name === 'AddCompanyIntroTemplate') {
      this.initialize();
    }
  },
};
</script>

<style scoped lang="scss">
h4 {
  margin-bottom: 25px;
}

label {
  font-weight: 500;
  font-size: 15px;
}

.setting-section-block {
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #dde1e5;
  border-radius: 4px;
  overflow: hidden;
  margin: auto;
  max-width: 800px;
  margin-bottom: 10px;

  @include respond(phone) {
    margin: 10px 10px;
  }

  & .section-middle {
    width: 100%;
    display: inline-block;
    position: relative;
    min-height: 65px;
    padding-top: 15px;
    & .profile-image {
      display: inline-block;
      position: absolute;
      top: -50px;
      left: 25px;
      cursor: pointer;
      & img {
        border: 3px solid #ffffff;
        width: 100%;
        min-width: 100px;
        min-height: 100px;
        max-width: 100px;
        max-height: 100px;
        border-radius: 50%;
        overflow: hidden;
      }
      & .upload-icon {
        position: absolute;
        right: 0;
        bottom: 0;
        box-sizing: border-box;
        border: 1.5px solid #ffffff;
        border-radius: 50%;
        width: 1.9375rem;
        height: 1.9375rem;
        background: #ebeef0;
        text-align: center;
        color: #636d77;
      }
    }
  }
}
</style>
